import Button from "@/components/Button";
import ListItem from "@/components/ListItem/ListItem";
import { renderNameFromAlgoliaHit } from "@/lib/personHelpers";
import { formatPhoneNumber } from "@/lib/phoneHelpers";
import { getPersonMatchHeadline, getPersonMatchSupportingContent } from "./matchContext";
import { PersonHit } from "./types";

export type RenderPersonOnlyHitOptions = {
  showContactDetails?: boolean;
  showSelectButton?: boolean;
  query?: string;
};

/**
 * Helper function to create initial UI state for person-only search
 */
export const createPersonOnlySearchUiState = (indexName: string) => {
  return {
    [indexName]: {
      configure: {
        filters: "object_type:Person",
        restrictSearchableAttributes: ["name", "preferred_name", "pga_id", "phone", "phones", "email"],
        attributesToHighlight: ["name", "preferred_name", "pga_id", "phone", "phones", "email"]
      }
    }
  };
};

export const getPersonSearchHitSupportingText = (
  hit: PersonHit,
  options: Pick<RenderPersonOnlyHitOptions, "showContactDetails"> = {}
): string | undefined => {
  const phone = hit.phone;
  const lines = options.showContactDetails
    ? [
        hit.pga_id ? `PGA ID: ${hit.pga_id}` : undefined,
        hit.section_name,
        hit.facility_name,
        phone ? formatPhoneNumber(phone) : undefined,
        hit.email
      ]
    : [hit.pga_id ? `PGA ID: ${hit.pga_id}` : undefined, hit.section_name, hit.facility_name];

  const supportingText = lines.filter(Boolean).join("\n");
  return supportingText || undefined;
};

/**
 * Helper function to render person-only search hits
 */
export const renderPersonOnlyHit = (
  hit: PersonHit,
  select: (hit: PersonHit) => void,
  options: RenderPersonOnlyHitOptions = {}
) => {
  const displayName = getPersonDisplayName(hit);
  const supportingText = getPersonMatchSupportingContent(hit, {
    showContactDetails: options.showContactDetails,
    query: options.query
  });

  return (
    <div key={hit.objectID} className="cursor-pointer" onClick={() => select(hit)}>
      <div className="flex items-stretch justify-between gap-4">
        <div className="min-w-0 flex-1">
          <ListItem
            compact
            useAvatar
            picture={hit.profile_photo}
            headline={getPersonMatchHeadline(hit, displayName)}
            supportingText={supportingText || undefined}
            longContent={options.showContactDetails || !!supportingText}
          />
        </div>
        {options.showSelectButton && (
          <div className="flex shrink-0 items-center" onClick={(event) => event.stopPropagation()}>
            <Button variant="text" onClick={() => select(hit)}>
              Select
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Helper function to get display name for person hits
 */
export const getPersonDisplayName = (hit: PersonHit): string => {
  return renderNameFromAlgoliaHit(hit.name, hit.preferred_name);
};

export const isPersonExactMatch = (hit: PersonHit, query: string): boolean => {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return false;
  return hit.pga_id?.toLowerCase() === normalizedQuery;
};

export function createPersonOnlyHitRenderer(
  options: Omit<RenderPersonOnlyHitOptions, "query"> = {}
): (hit: PersonHit, select: (hit: PersonHit) => void, query?: string) => JSX.Element {
  return (hit, select, query) => renderPersonOnlyHit(hit, select, { ...options, query });
}
